import { GridContainer } from '@bob/library'
import { CardSection } from '@websrc/App/Components/CardSection/CardSection'
import { CoralAnimatedCTA } from '@websrc/App/Components/CoralAnimatedCTA/CoralAnimatedCTA'
import { CoralQuoteLarge } from '@websrc/App/Components/CoralQuoteLarge/CoralQuoteLarge'
import { Download } from '@websrc/App/Components/Download/Download'
import { SubpageHero } from '@websrc/App/Components/SubpageHero/SubpageHero'
import { WithSuspense } from '@websrc/App/Components/WithSuspense'
import { CoralBannerCTA } from '@websrc/App/Pages/CoralBlogArticlePage/CoralBlogArticlePageBody/CoralBlogArticleBannerCTA/CoralBannerCTA'
import { CoralBlogArticleTable } from '@websrc/App/Pages/CoralBlogArticlePage/CoralBlogArticlePageBody/CoralBlogArticleTable/CoralBlogArticleTable'
import { mapSpot } from '@websrc/DataMappers/mapSpot'
import { CRMUnsubscribe } from '@websrc/Spot/CRMUnsubscribe/CRMUnsubscribe'
import { SpotDistributor } from '@websrc/Spot/Distributor/SpotDistributor'
import { SpotExpandable } from '@websrc/Spot/Expandable/SpotExpandable'
import { SpotHeroCarousel } from '@websrc/Spot/HeroCarousel/SpotHeroCarousel'
import React, { lazy } from 'react'
import { match } from 'ts-pattern'

import { PageHero } from '../Hero/PageHero/PageHero'
import { SpotCampaign } from './Campaign/SpotCampaign'
import { SpotCampaignGrid } from './CampaignGrid/SpotCampaignGrid'
import { SpotCaption } from './Caption/SpotCaption'
import { LearnMoreButtonType } from './Collapse/SpotCollapse'
import { SpotComparisonTable } from './ComparisonTable/SpotComparisonTable'
import { SpotContent } from './Content/SpotContent'
import { CoralBlogCards } from './CoralBlogCards/CoralBlogCards'
import { CoralCampaign } from './CoralCampaign/CoralCampaign'
import { CoralContent } from './CoralContent/CoralContent'
import { CoralCTA } from './CoralCTA/CoralCTA'
import { CoralDomain } from './CoralDomain/CoralDomain'
import { CoralFAQ } from './CoralFAQ/CoralFAQ'
import { CoralGrantsFundingCarouselCards } from './CoralGrantsFundingCarouselCards/CoralGrantsFundingCarouselCards'
import { CoralGrantsFundingColumnCards } from './CoralGrantsFundingColumnCards/CoralGrantsFundingColumnCards'
import { CoralGrantsFundingSingleCard } from './CoralGrantsFundingSingleCard/CoralGrantsFundingSingleCard'
import { CoralLandscapeImage } from './CoralLandscapeImage/CoralLandscapeImage'
import { CoralListOverview } from './CoralListOverview/CoralListOverview'
import { CoralPdpCompatibleWith } from './CoralPdpCompatibleWith/CoralPdpCompatibleWith'
import { CoralPdpHowItWorks } from './CoralPdpHowItWorks/CoralPdpHowItWorks'
import { CoralPdpStateStandards } from './CoralPdpStateStandards/CoralPdpStateStandards'
import { CoralPdpWhatsIncluded } from './CoralPdpWhatsIncluded/CoralPdpWhatsIncluded'
import { CoralProductCards } from './CoralProductCards/CoralProductCards'
import { CoralProductSellingPoints } from './CoralProductSellingPoints/CoralProductSellingPoints'
import { CoralQuoteCards } from './CoralQuoteCards/CoralQuoteCards'
import { CoralSKUOverview } from './CoralSKUOverview/CoralSKUOverview'
import { CoralSocialCards } from './CoralSocialCards/CoralSocialCards'
import { CoralSocialProof } from './CoralSocialProof/CoralSocialProof'
import { CoralStatCards } from './CoralStatCards/CoralStatCards'
import { CoralTeacherResourcesCards } from './CoralTeacherResourcesCards/CoralTeacherResourcesCards'
import { CoralTeacherResourcesCards2 } from './CoralTeacherResourcesCards2/CoralTeacherResourcesCards2'
import { CoralUVP } from './CoralUVP/CoralUVP'
import { CoralVideo } from './CoralVideo/CoralVideo'
import { SpotCurriculum } from './Curriculum/SpotCurriculum'
import { SpotForm } from './Form/SpotForm'
import { SpotHeroMSForm } from './HeroMSForm/SpotHeroMSForm'
import { SpotIconExplainer } from './IconExplainer/SpotIconExplainer'
import { SpotImpactCallouts } from './ImpactCallouts/SpotImpactCallouts'
import { SpotMarkdown } from './Markdown/SpotMarkdown'
import { SpotModularSelector } from './ModularSelector/SpotModularSelector'
import { SpotMSForm } from './MSForm/SpotMSForm'
import { SpotNavigationSelector } from './NavigationSelector/SpotNavigationSelector'
import { SpotPageMessage } from './PageMessage/SpotPageMessage'
import { SpotProductBand } from './ProductBand/SpotProductBand'
import { SpotProductBandHero } from './ProductBandHero/SpotProductBandHero'
import { SpotQuote } from './Quote/SpotQuote'
import { SpotSegmentSelector } from './SegmentSelector/SpotSegmentSelector'
import { SpotSwimlanes } from './Swimlanes/SpotSwimlanes'
import { SpotTableOfContent } from './TableOfContent/SpotTableOfContent'
import { SpotTestimonials } from './Testimonials/Testimonials'
import { SpotTextRows } from './TextRows/SpotTextRows'
import { SpotUniqueSellingPoints } from './UniqueSellingPoints/SpotUniqueSellingPoints'

export interface SpotProps {
  forceNoSpacing?: boolean
  imageLazy?: boolean
  spot: ReturnType<typeof mapSpot>
  LearnMoreButton?: LearnMoreButtonType
  spotIndex?: number
  renderCoralSpotSpacing?: boolean
}

// Suspense can only be used with lazy and the component has to be exported default
const DownloadSpot = lazy(() => {
  return import('./Download/SpotDownload')
})

// eslint-disable-next-line max-lines-per-function
export const Spot = ({
  spot,
  imageLazy,
  LearnMoreButton,
  spotIndex,
  renderCoralSpotSpacing,
}: SpotProps) => {
  return (
    match(spot)
      .with({ __dataType: 'campaignGrid' }, ({ campaign_grid }) => (
        <SpotCampaignGrid
          imageLazy={imageLazy}
          isTopOfPage={spotIndex === 0}
          LearnMoreButton={LearnMoreButton}
          spot={campaign_grid}
        />
      ))
      .with({ __dataType: 'download' }, ({ download }) => (
        <WithSuspense>
          <DownloadSpot LearnMoreButton={LearnMoreButton} spot={download} />
        </WithSuspense>
      ))
      .with({ __dataType: 'markdown' }, ({ markdown }) => (
        <SpotMarkdown LearnMoreButton={LearnMoreButton} spot={markdown} />
      ))
      .with({ __dataType: 'iconExplainer' }, ({ icon_explainer }) => (
        <SpotIconExplainer
          isTopOfPage={spotIndex === 0}
          LearnMoreButton={LearnMoreButton}
          spot={icon_explainer}
        />
      ))
      .with({ __dataType: 'quote' }, ({ quote }) => (
        <SpotQuote LearnMoreButton={LearnMoreButton} spot={quote} />
      ))
      .with({ __dataType: 'campaign' }, ({ campaign_spot }) => (
        <SpotCampaign
          isTopOfPage={spotIndex === 0}
          LearnMoreButton={LearnMoreButton}
          spot={campaign_spot}
        />
      ))
      .with({ __dataType: 'contentSpot' }, ({ content_spot }) => (
        <SpotContent LearnMoreButton={LearnMoreButton} spot={content_spot} />
      ))
      .with({ __dataType: 'comparisonTable' }, (comparison_table_spot) => (
        <SpotComparisonTable spot={comparison_table_spot} />
      ))
      .with({ __dataType: 'caption' }, ({ caption_spot }) => (
        <SpotCaption
          isTopOfPage={spotIndex === 0}
          LearnMoreButton={LearnMoreButton}
          spot={caption_spot}
        />
      ))
      .with({ __dataType: 'curriculum' }, ({ curriculum }) => (
        <SpotCurriculum LearnMoreButton={LearnMoreButton} spot={curriculum} />
      ))
      .with({ __dataType: 'productBand' }, ({ product_band }) => (
        <SpotProductBand
          isTopOfPage={spotIndex === 0}
          LearnMoreButton={LearnMoreButton}
          spot={product_band}
        />
      ))
      .with({ __dataType: 'swimlanes' }, (swimlanes) => (
        <SpotSwimlanes isTopOfPage={spotIndex === 0} spot={swimlanes} />
      ))
      .with({ __dataType: 'productBandHero' }, ({ product_band_hero }) => (
        <SpotProductBandHero spot={product_band_hero} spotIndex={spotIndex ?? 0} />
      ))
      .with({ __dataType: 'segmentSelector' }, ({ segment_selector }) => (
        <SpotSegmentSelector
          isTopOfPage={spotIndex === 0}
          LearnMoreButton={LearnMoreButton}
          spot={segment_selector}
        />
      ))
      .with({ __dataType: 'modularSelector' }, ({ modular_selector }) => (
        <SpotModularSelector LearnMoreButton={LearnMoreButton} spot={modular_selector} />
      ))
      .with({ __dataType: 'distributor' }, ({ distributor }) => (
        <SpotDistributor spot={distributor} />
      ))
      .with({ __dataType: 'heroCarousel' }, ({ hero_carousel }) => (
        <SpotHeroCarousel spot={hero_carousel} />
      ))
      .with({ __dataType: 'expandable' }, (expandable) => <SpotExpandable {...expandable} />)
      .with({ __dataType: 'navigationSelector' }, ({ navigation_selector }) => (
        <SpotNavigationSelector spot={navigation_selector} />
      ))
      .with({ __dataType: 'textRows' }, ({ text_rows }) => <SpotTextRows spot={text_rows} />)
      .with({ __dataType: 'msForm' }, ({ ms_form }) => <SpotMSForm spot={ms_form} />)
      .with({ __dataType: 'tableOfContent' }, ({ table_of_content }) => (
        <SpotTableOfContent spot={table_of_content} />
      ))
      .with({ __dataType: 'downloadSection' }, (data) => (
        <Download data={data} useBoxTypeText={true} />
      ))
      .with({ __dataType: 'uniqueSellingPoints' }, (data) => (
        <SpotUniqueSellingPoints
          isTopOfPage={spotIndex === 0}
          LearnMoreButton={LearnMoreButton}
          spot={data}
        />
      ))
      .with({ __dataType: 'testimonials' }, (data) => (
        <SpotTestimonials {...data} isTopOfPage={spotIndex === 0} />
      ))

      .with({ __dataType: 'impactCallouts' }, (data) => (
        <SpotImpactCallouts isTopOfPage={spotIndex === 0} spot={data} />
      ))
      .with({ __dataType: 'cardSection' }, (data) => (
        <GridContainer>
          <CardSection {...data} isTopOfPage={spotIndex === 0} />
        </GridContainer>
      ))
      .with({ __dataType: 'subpageHero' }, (data) => (
        <GridContainer>
          <SubpageHero {...data} isTopOfPage={spotIndex === 0} />
        </GridContainer>
      ))
      .with({ __dataType: 'heroMSForm' }, (spot) => <SpotHeroMSForm spot={spot} />)
      .with({ __dataType: 'unsubscribe_spot' }, (spot) => <CRMUnsubscribe {...spot} />)
      .with({ __dataType: 'form' }, (spot) => <SpotForm spot={spot} />)
      .with({ __dataType: 'pageHero' }, (spot) => <PageHero {...spot} />)
      .with({ __dataType: 'pageMessage' }, (spot) => <SpotPageMessage spot={spot} />)
      .with({ __dataType: 'coralWebCta' }, (data) => (
        <CoralCTA renderCoralSpotSpacing={renderCoralSpotSpacing} {...data} />
      ))
      .with({ __dataType: 'coralWebTable' }, (data) => (
        <CoralBlogArticleTable {...data} applySpotStyling={true} />
      ))
      .with({ __dataType: 'coralWebVideo' }, (data) => <CoralVideo {...data} />)
      .with({ __dataType: 'coralWebFaq' }, (data) => (
        <CoralFAQ renderCoralSpotSpacing={renderCoralSpotSpacing} {...data} />
      ))
      .with({ __dataType: 'coralWebProductSellingPoints' }, (data) => (
        <CoralProductSellingPoints {...data} />
      ))
      .with({ __dataType: 'coralWebSocialProof' }, (data) => (
        <CoralSocialProof renderCoralSpotSpacing={renderCoralSpotSpacing} {...data} />
      ))
      .with({ __dataType: 'coralWebSkuOverview' }, (data) => <CoralSKUOverview {...data} />)
      .with({ __dataType: 'coralWebCampaign' }, (data) => <CoralCampaign {...data} />)
      .with({ __dataType: 'coralWebListOverview' }, (data) => <CoralListOverview {...data} />)
      .with({ __dataType: 'coralWebLandscapeImage' }, (data) => <CoralLandscapeImage {...data} />)
      .with({ __dataType: 'coralWebContent' }, (data) => <CoralContent {...data} />)
      .with({ __dataType: 'coralWebPdpHowItWorks' }, (data) => <CoralPdpHowItWorks {...data} />)
      .with({ __dataType: 'coralWebPdpStateStandards' }, (data) => (
        <CoralPdpStateStandards {...data} />
      ))
      .with({ __dataType: 'coralWebPdpWhatSIncluded' }, (data) => (
        <CoralPdpWhatsIncluded {...data} />
      ))
      .with({ __dataType: 'coralWebPdpCompatibleWith' }, (data) => (
        <CoralPdpCompatibleWith {...data} />
      ))
      .with({ __dataType: 'coralWebBannerCta' }, (data) => <CoralBannerCTA {...data} />)
      .with({ __dataType: 'coralWebUvp' }, (data) => <CoralUVP {...data} />)
      .with({ __dataType: 'coralWebDomain' }, (data) => <CoralDomain {...data} />)
      .with({ __dataType: 'coralWebProductCards' }, (data) => <CoralProductCards {...data} />)
      .with({ __dataType: 'coralWebAnimatedCta' }, (data) => <CoralAnimatedCTA {...data} />)
      .with({ __dataType: 'coralWebBlogCards' }, (data) => <CoralBlogCards {...data} />)
      .with({ __dataType: 'coralWebStatCards' }, (data) => <CoralStatCards {...data} />)
      .with({ __dataType: 'coralWebTeacherResourcesCards' }, (data) => (
        <CoralTeacherResourcesCards {...data} />
      ))
      .with({ __dataType: 'coralWebTeacherResourcesCards2' }, (data) => (
        <CoralTeacherResourcesCards2 {...data} />
      ))
      .with({ __dataType: 'coralWebSocialCards' }, (data) => <CoralSocialCards {...data} />)
      .with({ __dataType: 'coralWebQuote' }, (data) => <CoralQuoteLarge {...data} />)
      .with({ __dataType: 'coralWebQuoteCards' }, (data) => <CoralQuoteCards {...data} />)
      .with({ __dataType: 'coralWebGrantsFundingSingleCard' }, (data) => (
        <CoralGrantsFundingSingleCard {...data} />
      ))
      .with({ __dataType: 'coralWebGrantsFundingColumnCards' }, (data) => (
        <CoralGrantsFundingColumnCards {...data} />
      ))
      .with({ __dataType: 'coralWebGrantsFundingCarouselCards' }, (data) => (
        <CoralGrantsFundingCarouselCards {...data} />
      ))
      // exhaustive should work here but doesn't because of unexpected data coming from contentstack
      .otherwise(() => null)
  )
}
